import type { TOC } from '@ember/component/template-only';
import { t } from 'ember-intl';
import { PhListNumbers, PhChatsCircle } from 'ember-phosphor-icons';
import SectionPanel from 'tio-ui/components/section-panel';
import type { TasProgramTemplateExtension as TASProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';

type InstructionsSignature = TASProgramTemplateModel['approverInstructions'];
type FaqsSignature = TASProgramTemplateModel['faqs'];
type FaqEntry = FaqsSignature[number];

const increment = (index: number) => index + 1;

// FAQs are dual-shape: legacy-authored entries use heading/body, new-builder
// entries use question/answer (see FaqsSignature). Read whichever is present —
// same tolerance the runtime FAQ display applies.
const faqQuestion = (faq: FaqEntry) => faq.question ?? faq.heading ?? '';
const faqAnswer = (faq: FaqEntry) => faq.answer ?? faq.body ?? '';

interface ReadoutInstructionsPanelSignature {
  Args: {
    /** Ordinal-sorted approver instructions (the ext's `programApproverInstructions`). */
    instructions: InstructionsSignature;
    /** Ordinal-sorted FAQs (the ext's `programFaqs`). */
    faqs: FaqsSignature;
  };
  Element: HTMLDivElement;
}

/** Static Instructions & FAQs read-out: numbered approver steps + FAQ cards. */
const ReadoutInstructionsPanel: TOC<ReadoutInstructionsPanelSignature> = <template>
  <div class="space-y-5" ...attributes>
    <SectionPanel
      @icon={{PhListNumbers}}
      @title={{t "program_builder.instructions.approver_instructions"}}
      @iconColor="violet"
    >
      <:body>
        {{#if @instructions.length}}
          <ol class="m-0 list-none space-y-3 p-0">
            {{#each @instructions as |instruction index|}}
              <li class="tio-row-top-tight">
                <span
                  class="tio-row-center h-6 w-6 shrink-0 rounded-full tio-bg-brand-soft tio-text-xs-semibold tio-fg-brand"
                >{{increment index}}</span>
                <p class="m-0 flex-1 text-sm">{{instruction.body}}</p>
              </li>
            {{/each}}
          </ol>
        {{else}}
          <p class="text-sm tio-fg-muted">{{t "program_builder.readout.no_instructions"}}</p>
        {{/if}}
      </:body>
    </SectionPanel>

    <SectionPanel
      @icon={{PhChatsCircle}}
      @title={{t "program_builder.instructions.faqs"}}
      @iconColor="violet"
    >
      <:body>
        {{#if @faqs.length}}
          <div class="space-y-4">
            {{#each @faqs as |faq|}}
              <div class="rounded-lg border tio-border-default p-3">
                <div class="tio-text-sm-semibold">{{faqQuestion faq}}</div>
                <p class="mb-0 mt-1 text-sm tio-fg-muted">{{faqAnswer faq}}</p>
              </div>
            {{/each}}
          </div>
        {{else}}
          <p class="text-sm tio-fg-muted">{{t "program_builder.readout.no_faqs"}}</p>
        {{/if}}
      </:body>
    </SectionPanel>
  </div>
</template>;

export default ReadoutInstructionsPanel;
